// Pure geometry/eligibility math for the continuous height-offset level system.
//
// No Three.js — this is the unit-tested core that LevelGenerator wires into the
// renderer. See docs/level-generation.md ("Continuous height offsets").
//
// Coordinate convention (world cells, matching the renderer): y=0 is the bottom
// row, y=sceneHeight-1 the top. A parsed `grid` is rows top-to-bottom (string
// order), so grid row r corresponds to world y = sceneHeight-1-r.

export const SCENE_WIDTH = 30;
export const SCENE_HEIGHT = 20;

// Vertical clearance (in cells) required above a template's tallest solid/hazard
// so the player can always jump. ~one max jump (≈5.2 cells) plus body; matches
// the headroom of the old hand-authored "raised" layouts.
export const JUMP_HEADROOM = 6;

// The height the selection bias gently pulls H back toward, so terrain
// random-walks within a playable band instead of drifting to an extreme.
export const H_TARGET = 4;

/**
 * Derive a template's placement geometry from its parsed grid.
 *
 * @param {string[][]} grid - rows top-to-bottom, each an array of cell chars.
 * @param {Object} [options]
 * @param {number} [options.sceneWidth=SCENE_WIDTH]
 * @param {Object} [options.metadata] - author overrides (contentTopRow,
 *   fillColumns) take precedence over derived values.
 * @returns {Object} geometry { entryFloorY, exitFloorY, lowestFloorY,
 *   contentTopRow, netRise, fillColumns, columnLowestBlockY, entryIsPit,
 *   exitIsPit }
 */
export function deriveTemplateGeometry(grid, options = {}) {
  const sceneWidth = options.sceneWidth ?? SCENE_WIDTH;
  // Templates are bottom-anchored: the LAST grid row is world cell 0 and rows
  // run upward. A template may be shorter than the play area (its empty top rows
  // are omitted) — cells above the grid are simply empty. So the geometry maps
  // off the grid's own height, not the fixed scene height.
  const sceneHeight = grid.length;
  const overrides = options.metadata ?? {};

  const worldY = (r) => sceneHeight - 1 - r;

  // World y of the topmost B in a column (the standing surface), or null.
  const columnSurfaceY = (x) => {
    for (let r = 0; r < sceneHeight; r++) {
      if (grid[r][x] === "B") return worldY(r);
    }
    return null;
  };

  // World y of the lowest B in a column (where a fill-skirt starts), or null.
  const columnLowestBlockY = [];
  for (let x = 0; x < sceneWidth; x++) {
    let lowest = null;
    for (let r = sceneHeight - 1; r >= 0; r--) {
      if (grid[r][x] === "B") {
        lowest = worldY(r);
        break;
      }
    }
    columnLowestBlockY[x] = lowest;
  }

  const entrySurface = columnSurfaceY(0);
  const exitSurface = columnSurfaceY(sceneWidth - 1);
  const entryIsPit = entrySurface === null;
  const exitIsPit = exitSurface === null;
  const entryFloorY = entrySurface ?? 0;
  const exitFloorY = exitSurface ?? 0;

  // Lowest standing surface across non-pit columns (pits are intentional death
  // gaps and must not count, else any template with a pit looks bottomless).
  let lowestFloorY = Infinity;
  for (let x = 0; x < sceneWidth; x++) {
    const s = columnSurfaceY(x);
    if (s !== null && s < lowestFloorY) lowestFloorY = s;
  }
  if (!Number.isFinite(lowestFloorY)) lowestFloorY = 0;

  // Highest world y holding a B/S or a projectile spawner (`p` horizontal, `P`
  // vertical) — spawners are solid boxes the player collides with. Both laser
  // chars (`L` vertical, `l` horizontal) are excluded: vertical beams are
  // tall-scaled and deliberately run off the top, and a horizontal beam is a
  // thin line the player times rather than an obstacle needing jump headroom.
  // The first grid row (smallest r) with such content gives the largest world y.
  let derivedContentTop = 0;
  for (let r = 0; r < sceneHeight; r++) {
    let found = false;
    for (let x = 0; x < sceneWidth; x++) {
      const c = grid[r][x];
      if (c === "B" || c === "S" || c === "p" || c === "P") {
        found = true;
        break;
      }
    }
    if (found) {
      derivedContentTop = worldY(r);
      break;
    }
  }
  const contentTopRow =
    typeof overrides.contentTopRow === "number"
      ? overrides.contentTopRow
      : derivedContentTop;

  // Per-column fill flags: explicit metadata override, else bottom-row B.
  let fillColumns;
  if (Array.isArray(overrides.fillColumns)) {
    fillColumns = overrides.fillColumns.slice(0, sceneWidth);
  } else {
    fillColumns = [];
    for (let x = 0; x < sceneWidth; x++) {
      fillColumns[x] = grid[sceneHeight - 1][x] === "B";
    }
  }

  return {
    entryFloorY,
    exitFloorY,
    lowestFloorY,
    contentTopRow,
    netRise: exitFloorY - entryFloorY,
    fillColumns,
    columnLowestBlockY,
    entryIsPit,
    exitIsPit,
  };
}

/**
 * Spawner containment check. HORIZONTAL projectile spawners ("p") fire left, so
 * each must have a block ("B") somewhere to its left in the same row — that
 * backstop is what stops the round inside its own scene instead of letting it
 * fly out of the frame. (VERTICAL spawners "P" fire up off the top of the
 * screen and need no horizontal backstop, so they are not checked here.)
 * Returns the offending spawner positions ([] = all contained). Operates on a
 * grid of rows where each row is indexable by column (array or string).
 */
export function findUncontainedSpawners(grid) {
  const offenders = [];
  for (let r = 0; r < grid.length; r++) {
    const row = grid[r];
    for (let x = 0; x < row.length; x++) {
      if (row[x] !== "p") continue;
      let hasBackstop = false;
      for (let xx = x - 1; xx >= 0; xx--) {
        if (row[xx] === "B") {
          hasBackstop = true;
          break;
        }
      }
      if (!hasBackstop) offenders.push({ row: r, col: x });
    }
  }
  return offenders;
}

/**
 * Vertical shift (in cells) to place a template so its entry floor lands at the
 * current cumulative height H. Every cell at local world-y `y` renders at y+dy.
 */
export function computeDy(geom, currentHeight) {
  return currentHeight - geom.entryFloorY;
}

/**
 * The cumulative height after placing this template at the current height.
 */
export function nextHeight(geom, currentHeight) {
  return geom.exitFloorY + computeDy(geom, currentHeight);
}

/**
 * Is a template placeable at the current height H without going off-screen?
 *  - floor stays visible: lowestFloorY + dy >= 0  (descents need height)
 *  - top fits with headroom: dy + contentTopRow + headroom <= sceneHeight-1
 */
export function isEligible(
  geom,
  currentHeight,
  jumpHeadroom = JUMP_HEADROOM,
  sceneHeight = SCENE_HEIGHT
) {
  const dy = computeDy(geom, currentHeight);
  const floorOk = geom.lowestFloorY + dy >= 0;
  const topOk = dy + geom.contentTopRow + jumpHeadroom <= sceneHeight - 1;
  return floorOk && topOk;
}
